import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { wrap } from "popmotion";
import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image"
import { getIproPropertyImageData } from "../../utils/getImageFromUrl"
import Box from "@mui/material/Box"
import IconButton from '@mui/material/IconButton'
import Icon from "../../utils/icon";
import WishList from "../wishlist/wishlist"
import { formatPropertyPageUrl } from "../../utils/formatPropertyPageUrl"
import { useWpProperties } from "../../utils/formatPropertyPageUrl";

const variants = {
  enter: (direction) => {
    return {
      //x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      display: 'none'
    };
  },
  center: {
    zIndex: 1,
    //x: 0,
    opacity: 1,
    display: 'block'
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      //x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      display: 'none'
    };
  }
};

/**
 * Experimenting with distilling swipe offset and velocity into a single variable, so the
 * less distance a user has swiped, the more velocity they need to register as a swipe.
 * Should accomodate longer swipes and short flicks without having binary checks on
 * just distance thresholds and velocity > 0.
 */
const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

export const Carousel = ({ images, orientation, bgData, page, direction, paginate, imageSize, autoPlay = true }) => {

  const wpProperties = useWpProperties();

  // We only have 3 images, but we paginate them absolutely (ie 1, 2, 3, 4, 5...) and
  // then wrap that within 0-2 to find our image ID in the array below. By passing an
  // absolute page index as the `motion` component's `key` prop, `AnimatePresence` will
  // detect it as an entirely new image. So you can infinitely paginate as few as 1 images.
  const imageIndex = wrap(0, images.length, page);


  let pathname = images.length > 0 && new URL(images[imageIndex].url).pathname;
  const image = {
    url: pathname ? pathname : "",
    // width: 1042,
    // height: 468,
  }

  useEffect(() => {
    if (!autoPlay) {
      return
    }
    const timer = setInterval(() => paginate(1), 3000)
    return () => clearInterval(timer)
  }, [paginate])

  const imageSizes = {
    sm: {
      width: 316,
      height: 211,
      aspectRatio: 316 / 211,
    },
    md: {
      width: 850,
      aspectRatio: 16 / 9,
    },
    lg: {
      width: 1009,
      height: 673,
      aspectRatio: 1009 / 673,
    },
    xl: {
      width: 1124,
      height: 468,
      aspectRatio: 329 / 156,
    }

  }
  const smImageData = getIproPropertyImageData({ image, layout: "full-width", aspectRatio: imageSizes.sm.aspectRatio, width: imageSizes.sm.width, height: imageSizes.sm.height, options: { quality: 50 } })
  const mdImageData = getIproPropertyImageData({ image, layout: "constrained", aspectRatio: imageSizes.md.aspectRatio, width: imageSizes.md.width, options: { quality: 50 } })
  const lgImageData = getIproPropertyImageData({ image, layout: "constrained", aspectRatio: imageSizes.lg.aspectRatio, width: imageSizes.lg.width, height: imageSizes.lg.height, options: { quality: 50 } })
  const xlImageData = getIproPropertyImageData({ image, layout: "constrained", aspectRatio: imageSizes.xl.aspectRatio, width: imageSizes.xl.width, height: imageSizes.xl.height, options: { quality: 50 } })


  //const imageData = getIproPropertyImageData({ image, layout: "fixed", width: image.width, height: image.height, options: { quality: 50 } })

  // Navigation Arrows

  function NextArrow() {
    return (
      <Icon name="slider-right" width="1rem" />
    )
  }

  function PrevArrow() {
    return (
      <Icon name="slider-left" width="1rem" />
    )
  }

  return (
    <Box sx={{ minHeight: '100%', height: '100%', position: 'relative', overflow: 'hidden', maxWidth: '100%' }}>
      <Box sx={{ minHeight: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ minHeight: '100%', height: '100%', overflow: 'hidden', position: 'relative', }}>
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={page}
              src={images[imageIndex]}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);

                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              style={{ position: 'relative', maxWidth: '100vw', height: '100%', overflow: 'hidden' }}
            >

              {images[imageIndex] && imageSize === "sm" &&
                <Link to={formatPropertyPageUrl(wpProperties, bgData.ref)} title={bgData.name}>
                  <Box sx={{ height: '100%' }} className="imageWrapper">
                    <GatsbyImage layout="constrained" alt={images[imageIndex].description} image={smImageData} />
                  </Box>
                </Link>
              }
              {images[imageIndex] && imageSize === "md" &&
                <Link to={formatPropertyPageUrl(wpProperties, bgData.ref)} title={bgData.name}>
                  <Box sx={{ height: '100%' }} className="imageWrapper">
                    <GatsbyImage layout="constrained" alt={images[imageIndex].description} image={mdImageData} />
                  </Box>
                </Link>
              }
              {images[imageIndex] && imageSize === "lg" &&
                <Link to={formatPropertyPageUrl(wpProperties, bgData.ref)} title={bgData.name}>
                  <Box sx={{ height: '100%' }} className="imageWrapper">
                    <GatsbyImage layout="constrained" alt={images[imageIndex].description} image={lgImageData} />
                  </Box>
                </Link>
              }
              {images[imageIndex] && imageSize === "xl" &&
                <Link to={formatPropertyPageUrl(wpProperties, bgData.ref)} title={bgData.name}>
                  <Box sx={{ height: '100%', minHeight: '100%' }} className="imageWrapper">
                    <GatsbyImage style={{ minHeight: '100%' }} layout="constrained" alt={images[imageIndex].description} image={xlImageData} />
                  </Box>
                </Link>
              }


              {/* <GatsbyImage style={{ maxWidth: '100%' }} alt={images[imageIndex].description} image={imageData} /> */}
            </motion.div>
          </AnimatePresence>


          <Box sx={{
            zIndex: 99,
            position: 'absolute',
            top: '1rem',
            right: {
              xs: '1.5rem', '& .svgWrapper': {
                fill: 'rgba(255, 255, 255, 0.9)'
              }
            }
          }}>
            <WishList {...bgData} />
          </Box>



          <IconButton aria-label="next" onClick={() => paginate(1)} sx={{
            position: 'absolute', zIndex: 99, top: '50%', transform: 'translateY(-50%)', right: '1rem',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '50%',
            width: '2rem',
            height: '2rem',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)'
            }
          }}>
            <NextArrow />
          </IconButton>


          <IconButton aria-label="previous" onClick={() => paginate(-1)} sx={{
            position: 'absolute', zIndex: 99, top: '50%', transform: 'translateY(-50%)', left: '1rem', backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '50%',
            width: '2rem',
            height: '2rem',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)'
            }
          }}>
            <PrevArrow />
          </IconButton>

        </Box>
      </Box>




    </Box >
  );
};